import { Link } from 'react-router-dom';
import { LOCALE_NAMES, LOCALES, type Locale } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { alternatePaths } from '@/meta';
import { cn } from '@/components/ui';
import { setCookie } from '@/lib/dom';
import { Brand } from './NavRail';
import { Icon } from './Icon';

/**
 * The top bar: the menu toggle and brand on mobile, the search entry and the language
 * switch everywhere (PRD §28).
 *
 * Search is a *button*, not an inline field. Phase 10 gives it an overlay with recent
 * searches and a `/` shortcut, and two search inputs on one page — one in the header,
 * one in the overlay — is two places for a reader's query to live.
 */
export function Header({
  onOpenMenu,
  onOpenSearch,
  currentPath,
}: {
  onOpenMenu: () => void;
  onOpenSearch: () => void;
  currentPath: string;
}) {
  const { t, locale, alternates: activeAlternates } = useLocale();

  const alternates = activeAlternates ?? alternatePaths(currentPath);
  const other = LOCALES.find((candidate) => candidate !== locale) as Locale;
  const targetPath = alternates[other] || `/${other}`;

  return (
    <header
      className="flex items-center gap-3 border-b border-line bg-white px-gutter py-3"
      data-print-hide
    >
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t('nav.menu')}
        className="rounded-md p-1.5 text-ink transition hover:text-red md:hidden"
      >
        <Icon name="menu" size={22} />
      </button>

      <div className="md:hidden">
        <Brand compact />
      </div>

      <button
        type="button"
        onClick={onOpenSearch}
        className={cn(
          'ml-auto flex items-center gap-2 rounded-md border border-line bg-soft px-3 py-2',
          'text-base text-muted transition hover:border-red hover:text-red sm:w-64 sm:justify-start',
        )}
      >
        <Icon name="search" size={15} />
        <span className="hidden sm:inline">{t('search.placeholder')}</span>
        <span className="sr-only sm:hidden">{t('action.search')}</span>
        {/* The shortcut is advertised where a keyboard user will see it. */}
        <kbd className="ml-auto hidden rounded-xs border border-line bg-white px-1.5 py-0.5 text-2xs font-semibold text-muted sm:inline">
          /
        </kbd>
      </button>

      {/*
        A plain link, not a fetch: switching language is a navigation to the same path (or translated article)
        in the other locale, and the client/server cookie remembers the choice (§1.15).
      */}
      <Link
        to={targetPath}
        hrefLang={other}
        onClick={() => {
          setCookie('nk_locale', other);
        }}
        className={cn(
          'shrink-0 rounded-md border border-line px-3 py-2 text-xs font-bold text-ink transition',
          'hover:border-red hover:text-red',
        )}
      >
        {LOCALE_NAMES[other]}
      </Link>
    </header>
  );
}
